export type TypedDealCollection =
  | "deal_stays"
  | "deal_packages"
  | "deal_flights"
  | "deal_cruises"
  | "deal_transfers"
  | "deal_experiences"
  | "deal_services"
  | "deal_media";

export interface DealExtraField {
  name: string;
  value: unknown;
}

export interface TypedDealCategoryPrimary {
  slug: string;
  group_slug: string;
  group_display_name?: string | null;
  sub_slugs: string[];
  sub_display_names: string[];
}

export interface TypedDealCategorySecondary {
  slug: string;
  group_slug?: string | null;
  name?: string | null;
}

export interface TypedDealCategoryPackage {
  primary_slug?: string | null;
  secondary_slugs: string[];
}

export interface TypedDealCategories {
  primary: TypedDealCategoryPrimary;
  secondary: TypedDealCategorySecondary[];
  package?: TypedDealCategoryPackage;
}

export interface GalleryItem {
  key: string;
  media_type: "image" | "video" | "doc_preview" | "pdf_preview";
  status: "pending" | "approved" | "rejected";
  rejection_reason?: string | null;
  uploaded_at?: string;
  uploaded_by?: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface TypedDealMedia {
  image?: string | null;
  image_clean?: string | null;
  pdf_url?: string | null;
  video_url?: string | null;
  gallery: GalleryItem[];
}

export interface TypedDealDestination {
  cities: string[];
  states: string[];
  countries: string[];
  city?: string;
  country?: string;
}

export interface TypedDealValidity {
  dates?: string[];
  validity_periods?: Record<string, unknown>[];
  valid_from?: string | null;
  valid_until?: string | null;
  booking_deadline?: string | null;
  travel_period?: Record<string, unknown> | null;
}

export interface DmcServicesPreferences {
  countries: string[];
  cities: string[];
  tags: string[];
  activities: string[];
  min_pax: number[];
  max_pax: number[];
}

export interface DmcServices {
  seller_types: string[];
  seller_preferences: DmcServicesPreferences;
  personality: string[];
  is_verified: boolean;
}

export interface TypedDealDealer {
  id: string;
  /** Masked on public listings, e.g. "Oasis Traveller" -> "O*************r". */
  name: string;
  city?: string | null;
  country?: string | null;
  state?: string | null;
  is_verified?: boolean;
  /** Public trust label shown next to the verified badge — admin-editable, defaults to "Gullivr Verified Seller". */
  verified_name?: string;
  dmc_services?: DmcServices;
}

export interface TypedDealLifecycle {
  status: string;
  priority: string;
  is_active: boolean;
  is_featured: boolean;
  is_verified: boolean;
}

export interface TypedDealAdminReview {
  status: "pending" | "approved" | "rejected";
  flagged_reason?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  rejection_reason?: string | null;
}

export interface TypedDealTerms {
  min_pax?: number | null;
  max_pax?: number | null;
  advance_booking?: string | null;
  cancellation_policy?: string | null;
  blackout_dates?: string[];
}

export interface TypedDealInventory {
  total?: number | null;
  remaining?: number | null;
  cancelled?: number;
  unit?: "seats" | "rooms" | "cabins" | "slots" | null;
}

export interface TypedDealBase {
  _id?: string;
  id?: string;
  source_deal_id?: string | null;
  categories: TypedDealCategories;
  item_type: string;
  category_slug: string;
  title: string;
  description: string;
  user_title?: string | null;
  user_description?: string | null;
  enriched_summary?: string | null;
  destination: TypedDealDestination;
  validity: TypedDealValidity;
  tags: string[];
  extras: DealExtraField[];
  media: TypedDealMedia;
  dealer: TypedDealDealer;
  lifecycle: TypedDealLifecycle;
  admin_review?: TypedDealAdminReview | null;
  likes: number;
  is_liked?: boolean;
  save_count?: number;
  terms?: TypedDealTerms | null;
  inventory?: TypedDealInventory | null;
  is_flash?: boolean;
  flash_ends_at?: string | null;
  rail_tags?: string[];
  ai_extracted_raw?: Record<string, unknown> | null;
  created_by: string;
  updated_by?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export type Deal = Partial<TypedDealBase> & {
  id?: string;
  title: string;
  description: string;
  item_type: string;
  created_at?: string;
  is_boosted?: boolean;
  boost_count?: number;
  boost_campaign_id?: string | null;
  collection?: TypedDealCollection | string;
  /** Set by the typed-inventory bridge — authoritative after AI reclassification. */
  typed_inventory_collection?: string;
  deal_record_collection?: string;
  // Temporary denormalized fields for non-typed feeds (news/webinar/contact) and in-flight UI migration.
  category?: string;
  category_group?: string;
  image?: string;
  image_clean?: string;
  video_url?: string;
  pdf_url?: string;
  dealer_id?: string;
  dealer_name?: string;
  dealer_city?: string;
  dealer_country?: string;
  dealer_state?: string;
  dealer_phone?: string;
  dealer_email?: string;
  dealer_whatsapp?: string;
  ai_suggested_title?: string;
  ai_suggested_description?: string;
  ai_used_suggested_title?: boolean;
  ai_used_suggested_description?: boolean;
  /** Server-derived: whether the "Also publish as Package" toggle should be offered.
   *  False when the deal already lives in deal_packages (already a package). */
  package_toggle_applicable?: boolean;
  webinar_link?: string;
  webinar_date?: string;
  presenter?: string;
  original_price?: number;
  discounted_price?: number;
  stay?: Record<string, unknown>;
  package?: Record<string, unknown>;
  flight?: Record<string, unknown>;
  cruise?: Record<string, unknown>;
  transfer?: Record<string, unknown>;
  experience?: Record<string, unknown>;
  media_asset?: Record<string, unknown>;
  service_type?: string;
  visa?: Record<string, unknown>;
  insurance?: Record<string, unknown>;
  custom?: Record<string, unknown>;
  pricing?: {
    original_price?: number;
    discounted_price?: number;
    currency?: string;
    [key: string]: unknown;
  };
  deal_record_status?: string;
  seller_type_approval_reason?: string;
  seller_type_approval_ai_group?: string;
  seller_type_approval_seller_type?: string;
  seller_type_approval_allowed_groups?: string[];
  /** True for shadow "deals" rows surfaced in My Inventory because AI analysis
   *  rejected them / promotion failed and no typed doc was ever created.
   *  These have no `collection`, so edit/delete/boost actions don't apply. */
  _stuck_shadow_deal?: boolean;
  needs_manual_recategorisation_reason?: string;
};

export interface HomeDealBannerField {
  key: string;
  label: string;
  type: "text" | "number";
  position: "left_tag" | "right_upper_tag" | "highlighted_tag" | "custom";
  value: string;
}

/** Public shape of GET /api/home-deal-banners — admin-curated home page banners. */
export interface HomeDealBanner {
  id: string;
  category_slug: string;
  subcategory_slug?: string | null;
  title?: string | null;
  image_url?: string | null;
  fields: HomeDealBannerField[];
  is_deal_of_day: boolean;
  banner_type?: "category" | "seller_deal" | "direct_link";
  seller_id?: string | null;
  seller_name?: string | null;
  seller_email?: string | null;
  seller_role?: string | null;
  deal_id?: string | null;
  deal_collection?: string | null;
  deal_title?: string | null;
  direct_link?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at?: string | null;
}

export interface CategoryV2 {
  _id?: string;
  slug: string;
  name: string;
  group_slug?: string | null;
  display_group?: string | null;
  parent_slug?: string | null;
  level: number;
  item_types: string[];
  is_promotional: boolean;
  description?: string | null;
  keywords: string[];
  icon?: string | null;
  hero_image_url?: string | null;
  hero_video_url?: string | null;
  is_active: boolean;
  is_promoted: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface TypedDealMutationResponse {
  id: string;
  collection: TypedDealCollection | string;
  document: Deal;
}

/** Same as typed create plus optional legacy shadow doc after immediate upload. */
export interface TypedDealCreateWithUploadResponse extends TypedDealMutationResponse {
  upload?: unknown;
}

export interface DealLike {
  user_id: string;
  deal_id: string;
  collection: string;
  created_at: string;
}

export function dealId(deal: Deal): string {
  return String(deal.id || deal._id || "");
}

export function dealCollection(deal: Deal): string {
  // Prefer the authoritative bridge field — updated after AI reclassification.
  if (deal.typed_inventory_collection) return String(deal.typed_inventory_collection);
  if (deal.deal_record_collection) return String(deal.deal_record_collection);
  if (deal.collection) return String(deal.collection);
  const byGroup: Record<string, TypedDealCollection> = {
    stays: "deal_stays",
    packages: "deal_packages",
    flights: "deal_flights",
    cruises: "deal_cruises",
    transfers: "deal_transfers",
    experiences: "deal_experiences",
    services: "deal_services",
    media: "deal_media",
  };
  return byGroup[deal.categories?.primary?.group_slug || ""] || "";
}
